import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface SenseSphereUserInfo {
    id: number;
    uuid: string;
    username: string;
    first_name: string;
    last_name: string;
    full_name: string;
    email: string;
    avatar: string | null;
}

export interface SenseSphereSystemInfo {
    name: string;
    url: string;
    logo: string;
    favicon: string;
    support_email: string;
    description: string;
}

export interface SenseSphereBotInfo {
    uuid: string;
    name: string;
    avatar: string | null;
    description: string;
    is_active: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class SenseSphereOAuthService {
    private readonly SPHERE_URL = environment.chatbot.baseUrl;
    private readonly PARTNER_KEY = 'ccms';
    private readonly CALLBACK_URI = `${window.location.origin}/auth/sensesphere/callback`;

    private readonly TOKEN_KEY = 'sensesphere_token';
    private readonly USER_ID_KEY = 'sensesphere_user_id';
    private readonly USER_INFO_KEY = 'sensesphere_user_info';

    constructor(private http: HttpClient) {}

    login(): void {
        const redirectUri = encodeURIComponent(this.CALLBACK_URI);
        const sphereUrl = `${this.SPHERE_URL}/auth/sensesphere-auth/`;
        const fullUrl = `${sphereUrl}?partner_key=${this.PARTNER_KEY}&redirect_uri=${redirectUri}`;
        
        // Use window.open to force navigation and bypass any Angular router interception
        const newWindow = window.open(fullUrl, '_self');
        
        if (!newWindow) {
            console.warn('[OAuth] window.open was blocked by popup blocker, falling back to location.assign');
            window.location.assign(fullUrl);
        }
    }

    handleCallback(): { token: string; userId: string; isNewUser: boolean } | null {
        const params = new URLSearchParams(window.location.search);

        const token = params.get('token');
        const userId = params.get('user_id');
        const isNewUser = params.get('created') === 'true';

        if (!token || !userId) {
            console.error('Missing token or user_id in callback URL');
            return null;
        }

        localStorage.setItem(this.TOKEN_KEY, token);
        localStorage.setItem(this.USER_ID_KEY, userId);

        window.history.replaceState({}, document.title, '/');

        return { token, userId, isNewUser };
    }

    async fetchUserInfo(token: string, userId: string): Promise<SenseSphereUserInfo | null> {
        try {
            const response = await firstValueFrom(
                this.http.get<{ user: SenseSphereUserInfo }>(
                    `${this.SPHERE_URL}/api/partner/user-info/`,
                    { params: { token, user_uuid: userId } }
                )
            );

            if (response?.user) {
                localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(response.user));
                return response.user;
            }
        } catch (error) {
            console.error('Failed to fetch user info:', error);
        }
        return null;
    }

    isAuthenticated(): boolean {
        return !!localStorage.getItem(this.TOKEN_KEY);
    }

    getToken(): string | null {
        return localStorage.getItem(this.TOKEN_KEY);
    }

    getUserId(): string | null {
        return localStorage.getItem(this.USER_ID_KEY);
    }

    getUserInfo(): SenseSphereUserInfo | null {
        const info = localStorage.getItem(this.USER_INFO_KEY);
        return info ? JSON.parse(info) : null;
    }

    private systemInfoCache: SenseSphereSystemInfo | null = null;
    private botInfoCache = new Map<string, SenseSphereBotInfo | null>();

    async fetchSystemInfo(): Promise<SenseSphereSystemInfo | null> {
        if (this.systemInfoCache) {
            return this.systemInfoCache;
        }

        try {
            const response = await firstValueFrom(
                this.http.get<SenseSphereSystemInfo>(
                    `${this.SPHERE_URL}/api/partner/system-info/`
                )
            );

            if (response) {
                this.systemInfoCache = response;
                return response;
            }
        } catch (error) {
            console.error('Failed to fetch system info:', error);
        }
        return null;
    }

    getSystemInfo(): SenseSphereSystemInfo | null {
        return this.systemInfoCache;
    }

    async fetchBotInfo(botUuid: string): Promise<SenseSphereBotInfo | null> {
        if (this.botInfoCache.has(botUuid)) {
            return this.botInfoCache.get(botUuid) || null;
        }

        try {
            const response = await firstValueFrom(
                this.http.get<SenseSphereBotInfo>(
                    `${this.SPHERE_URL}/api/partner/bot/${botUuid}/`
                )
            );

            if (response) {
                this.botInfoCache.set(botUuid, response);
                return response;
            }
        } catch (error) {
            console.error('Failed to fetch bot info:', error);
        }
        this.botInfoCache.set(botUuid, null);
        return null;
    }

    getBotInfo(botUuid: string): SenseSphereBotInfo | null {
        return this.botInfoCache.get(botUuid) || null;
    }

    logout(): void {
        localStorage.removeItem(this.TOKEN_KEY);
        localStorage.removeItem(this.USER_ID_KEY);
        localStorage.removeItem(this.USER_INFO_KEY);
        this.clearChatbotSession();
    }

    private clearChatbotSession(): void {
        const keys = Object.keys(localStorage).filter(k => k.startsWith('chatbot:'));
        keys.forEach(k => localStorage.removeItem(k));
    }
}
